import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Props {
  aiScoreWeight: number;
  error: string | null;
  financialScoreWeight: number;
  isDirty: boolean;
  isLoading: boolean;
  isScoreTotalValid: boolean;
  minimumDownPaymentRate: number;
  scoreTotal: number;
  onAiScoreWeightChange: (next: number) => void;
  onCancel: () => void;
  onFinancialScoreWeightChange: (next: number) => void;
  onSave: () => Promise<void>;
}

const MasterParametersSection = ({
  aiScoreWeight,
  error,
  financialScoreWeight,
  isDirty,
  isLoading,
  isScoreTotalValid,
  minimumDownPaymentRate,
  scoreTotal,
  onAiScoreWeightChange,
  onCancel,
  onFinancialScoreWeightChange,
  onSave,
}: Props): ReactElement => {
  const [financialDraft, setFinancialDraft] = useState(financialScoreWeight.toString());
  const [aiDraft, setAiDraft] = useState(aiScoreWeight.toString());

  useEffect(() => {
    setFinancialDraft(financialScoreWeight.toString());
  }, [financialScoreWeight]);
  useEffect(() => {
    setAiDraft(aiScoreWeight.toString());
  }, [aiScoreWeight]);

  const commitFinancial = (): void => {
    const next = parseFloat(financialDraft);
    if (Number.isFinite(next) && next >= 0) onFinancialScoreWeightChange(next);
    else setFinancialDraft(financialScoreWeight.toString());
  };

  const commitAi = (): void => {
    const next = parseFloat(aiDraft);
    if (Number.isFinite(next) && next >= 0) onAiScoreWeightChange(next);
    else setAiDraft(aiScoreWeight.toString());
  };

  const betaSum = Math.round(scoreTotal * 100);
  const interestNoAi = Math.round((1 - financialScoreWeight) * 100);
  const interestWithAi = Math.round((1 - financialScoreWeight - aiScoreWeight) * 100);

  return (
    <div className="flex flex-col gap-0">
      <p className="text-xs font-medium text-text-subtle uppercase tracking-wider mb-2.5 mt-1">
        Condiciones de venta (Base Global)
      </p>

      <div className="flex flex-col mb-5">
        <div className="flex items-center gap-2.5 py-2.5">
          <span className="flex-1 text-sm text-text-default">Pie mínimo</span>
          <span className="text-sm text-text-muted">
            {minimumDownPaymentRate !== null && minimumDownPaymentRate !== undefined
              ? `${minimumDownPaymentRate * 100}%`
              : '- (Default: 20%)'}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-2.5">
        <p className="text-xs font-medium text-text-subtle uppercase tracking-wider">Betas de puntaje</p>
        <span className={`text-xs font-medium tabular-nums ${isScoreTotalValid ? 'text-success' : 'text-error'}`}>
          Total: <strong>{betaSum}</strong>%
        </span>
      </div>

      <p className="text-xs text-text-muted mb-3">Los valores no deben superar 1.00.</p>

      {!isScoreTotalValid && (
        <div className="flex items-center gap-2 mb-3 text-xs text-error bg-error-light border border-error-light rounded-lg px-3 py-2">
          Las betas superan 1.00. Corrige los valores antes de guardar.
        </div>
      )}

      <div className="bg-bg-surface-raised border border-border-subtle rounded-lg p-3 mb-5">
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <p className="text-xs text-text-subtle mb-2">Beta puntaje financiero</p>
            <div className="flex items-center gap-2">
              <span className="text-base font-medium text-text-default tabular-nums">
                {Math.round(financialScoreWeight * 100)}%
              </span>
              <Input
                type="number"
                step="0.01"
                min={0}
                max={1}
                className="w-20"
                value={financialDraft}
                onChange={(e) => setFinancialDraft(e.target.value)}
                onBlur={commitFinancial}
              />
            </div>
          </div>
          <div>
            <p className="text-xs text-text-subtle mb-2">Beta puntaje AI</p>
            <div className="flex items-center gap-2">
              <span className="text-base font-medium text-text-default tabular-nums">
                {Math.round(aiScoreWeight * 100)}%
              </span>
              <Input
                type="number"
                step="0.01"
                min={0}
                max={1}
                className="w-20"
                value={aiDraft}
                onChange={(e) => setAiDraft(e.target.value)}
                onBlur={commitAi}
              />
            </div>
          </div>
        </div>
        <div className="mt-3 border-t border-border-subtle pt-3">
          <p className="text-xs text-text-subtle mb-2">Beta puntaje interés restante</p>
          <div className="grid grid-cols-2 gap-3 text-sm text-text-subtle">
            <p>
              Sin aiScore: <span className="font-medium text-text-default tabular-nums">{interestNoAi}%</span>
            </p>
            <p>
              Con aiScore: <span className="font-medium text-text-default tabular-nums">{interestWithAi}%</span>
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 mb-3 text-xs text-error bg-error-light border border-error-light rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        {isDirty && (
          <span className="text-xs text-error flex items-center gap-1">
            <span>●</span> Cambios sin guardar
          </span>
        )}
        <Button type="button" onClick={onCancel} disabled={isLoading} variant="outline">
          Cancelar
        </Button>
        <Button
          type="button"
          onClick={() => {
            void onSave();
          }}
          disabled={!isDirty || !isScoreTotalValid || isLoading}
        >
          {isLoading ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </div>
    </div>
  );
};

export default MasterParametersSection;
